import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtINR, STAGE_LABEL } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Phone, Mail, Globe, MapPin, ArrowLeft, Plus, CalendarClock, FileText, Star, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import NewFollowupDialog from "@/components/NewFollowupDialog";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";
import { NewQuotationWizard, STATUS_LABEL, STATUS_COLOR } from "./Quotations";
import useSmartBack from "@/hooks/useSmartBack";

// Add / Update the customer's billing address (corrections doc) — full form
// incl. GSTIN & Place of Supply; saves via PATCH /clients and refreshes the
// profile immediately.
function BillingAddressDialog({ client, open, onOpenChange, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !client) return;
    const ba = client.billing_address || {};
    setF({
      attention: ba.attention || "", line1: ba.line1 || "", line2: ba.line2 || "",
      city: ba.city || "", state: ba.state || "", pincode: ba.pincode || "",
      country: ba.country || "India", phone: ba.phone || "",
      gstin: client.gst_number || "", state_code: client.state_code || "",
      state_name: client.state_name || "",
    });
  }, [open, client]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/clients/${client.id}`, {
        company_name: client.company_name,
        gst_number: f.gstin || null,
        state_code: f.state_code || null,
        state_name: f.state_name || null,
        billing_address: {
          attention: f.attention || null, line1: f.line1 || null, line2: f.line2 || null,
          city: f.city || null, state: f.state || null, pincode: f.pincode || null,
          country: f.country || null, phone: f.phone || null,
        },
      });
      toast.success("Billing address saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  const F = ({ label, k, ph }) => (
    <div>
      <Label className="text-xs font-semibold">{label}</Label>
      <Input className="rounded-2xl h-10 mt-1" value={f[k] || ""} onChange={set(k)} placeholder={ph || ""} data-testid={`billing-${k}`} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="billing-address-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {(client?.billing_address?.line1 || client?.billing_address?.city) ? "Update Address" : "Add Address"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">{F({ label: "Attention / Contact Person", k: "attention" })}</div>
          <div className="sm:col-span-2">{F({ label: "Address Line 1", k: "line1" })}</div>
          <div className="sm:col-span-2">{F({ label: "Address Line 2", k: "line2" })}</div>
          {F({ label: "City", k: "city" })}
          {F({ label: "State", k: "state" })}
          {F({ label: "Pincode", k: "pincode" })}
          {F({ label: "Country", k: "country" })}
          {F({ label: "Phone", k: "phone" })}
          {F({ label: "GSTIN", k: "gstin", ph: "e.g. 36AABCU8120H1ZF" })}
          {F({ label: "Place of Supply — State Code", k: "state_code", ph: "e.g. 36" })}
          {F({ label: "Place of Supply — State Name", k: "state_name", ph: "e.g. Telangana" })}
        </div>
        <Button onClick={save} disabled={busy} className="rounded-2xl w-full mt-2 tap" data-testid="billing-save-button">
          {busy ? "Saving…" : "Save Address"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const goBack = useSmartBack("/clients");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFu, setOpenFu] = useState(false);
  const [openAddr, setOpenAddr] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [openQuote, setOpenQuote] = useState(false);

  const loadQuotes = useCallback(async () => {
    try {
      const { data } = await api.get("/quotations", { params: { client_id: id } });
      setQuotes(data || []);
    } catch { setQuotes([]); }
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${id}`);
      setData(data);
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); loadQuotes(); }, [load, loadQuotes]);

  if (loading) return <div className="p-6"><Skeleton className="h-12 w-1/2 mb-4" /><Skeleton className="h-64 rounded-3xl" /></div>;
  if (!data) return <div className="p-6">Not found.</div>;

  const c = data.client;
  const ba = c.billing_address || {};
  const dialPhone = c.phone || ba.phone || "";
  const fullAddress = [ba.line1, ba.line2, ba.city, ba.state, ba.pincode, ba.country].filter(Boolean).join(", ");
  const hasAddress = !!(ba.line1 || ba.city || ba.state);

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto">
      <button type="button" onClick={goBack} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground mb-4 tap" data-testid="back-clients">
        <ArrowLeft className="h-3 w-3" /> Back
      </button>

      <Card className="rounded-3xl border-border/60 shadow-soft mb-6">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-wrap items-start gap-5">
            <div className="h-16 w-16 rounded-3xl bg-primary-50 text-primary-700 grid place-items-center">
              <Building2 className="h-7 w-7" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">{c.industry || c.contact_person || "Client"}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">{c.company_name}</h1>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-muted-foreground">
                {(() => {
                  const loc = [c.district, c.state].filter(Boolean).join(", ") || c.location;
                  return loc ? <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{loc}</span> : null;
                })()}
                {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>}
                {dialPhone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{dialPhone}</span>}
                {c.website && <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" />{c.website}</span>}
              </div>
              {(dialPhone || c.email) && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <CallButton phone={dialPhone} testId="call-client-header" />
                  <WhatsAppButton
                    phone={dialPhone}
                    email={c.email}
                    contactName={c.contact_person || c.company_name}
                    clientId={c.id}
                    clientCompany={c.company_name}
                    defaultTemplate="intro"
                    label="Send Message"
                    testId="wa-client-header"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge variant="outline" className="bg-primary-50 text-primary-700 border-primary-200 uppercase text-[10px] tracking-wider capitalize">
                {(c.customer_type || "").replace(/_/g, " ")}
              </Badge>
              {/* Generate a fresh lead from this customer — usable again and
                  again (each click starts a brand-new prefilled lead). */}
              <Button asChild className="rounded-2xl tap" data-testid="client-new-lead-button">
                <Link to={`/leads?new=1&name=${encodeURIComponent(c.contact_person || "")}&company=${encodeURIComponent(c.company_name || "")}&email=${encodeURIComponent(c.email || "")}&phone=${encodeURIComponent(dialPhone || "")}&industry=${encodeURIComponent(c.industry || "")}&source=existing_user`}>
                  <Plus className="h-4 w-4 mr-1" /> New Lead
                </Link>
              </Button>
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {(c.gst_number || c.state_name || c.state_code || c.contact_person) && (
              <div className="p-4 rounded-2xl bg-muted/30 border border-border/60">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Company details</p>
                <dl className="space-y-1.5 text-sm">
                  {c.contact_person && (<div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Contact</dt><dd className="font-medium">{c.contact_person}</dd></div>)}
                  {c.gst_number && (<div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">GSTIN</dt><dd className="font-mono text-xs">{c.gst_number}</dd></div>)}
                  {(c.state_name || c.state_code) && (<div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Place of supply</dt><dd>{[c.state_code, c.state_name].filter(Boolean).join(" — ")}</dd></div>)}
                </dl>
              </div>
            )}
            <div className="p-4 rounded-2xl bg-muted/30 border border-border/60">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Billing address</p>
                <Button variant="outline" size="sm" className="rounded-xl h-7 text-[11px] px-2.5 tap" onClick={() => setOpenAddr(true)} data-testid="billing-address-button">
                  {hasAddress ? <><Pencil className="h-3 w-3 mr-1" /> Update Address</> : <><Plus className="h-3 w-3 mr-1" /> Add Address</>}
                </Button>
              </div>
              {hasAddress || ba.attention || ba.phone ? (
                <dl className="space-y-1.5 text-sm">
                  {ba.attention && (<div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Attention</dt><dd>{ba.attention}</dd></div>)}
                  {fullAddress && (<div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Address</dt><dd>{fullAddress}</dd></div>)}
                  {ba.phone && (<div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Phone</dt><dd>{ba.phone}</dd></div>)}
                </dl>
              ) : (
                <p className="text-xs text-muted-foreground italic">No billing address yet.</p>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-4 gap-4 mt-7">
            <div className="p-4 rounded-2xl bg-muted/40">
              <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Active deals</p>
              <p className="font-display text-xl sm:text-2xl font-black mt-1">{(data.deals || []).filter(d => !["won", "lost"].includes(d.stage)).length}</p>
            </div>
            <div className="p-4 rounded-2xl bg-muted/40">
              <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Pipeline value</p>
              <p className="font-display text-xl sm:text-2xl font-black mt-1">{fmtINR((data.deals || []).filter(d => !["won", "lost"].includes(d.stage)).reduce((a, d) => a + Number(d.value || 0), 0))}</p>
            </div>
            <div className="p-4 rounded-2xl bg-muted/40">
              <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Pending follow-ups</p>
              <p className="font-display text-xl sm:text-2xl font-black mt-1">{(data.followups || []).length}</p>
            </div>
            <div className="p-4 rounded-2xl bg-muted/40">
              <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Last interaction</p>
              <p className="text-sm font-semibold mt-1.5">{c.last_interaction_at ? fmtDateTime(c.last_interaction_at) : "—"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2 mb-3">
        <Button variant="outline" onClick={() => setOpenQuote(true)} className="rounded-2xl tap" data-testid="new-quotation-client">
          <FileText className="h-4 w-4 mr-1" /> New quotation
        </Button>
        <Button onClick={() => setOpenFu(true)} className="rounded-2xl tap" data-testid="add-followup-client"><Plus className="h-4 w-4 mr-1" /> Schedule follow-up</Button>
      </div>

      <Tabs defaultValue="deals">
        <TabsList className="rounded-2xl mb-4">
          <TabsTrigger value="deals" className="rounded-xl">Deals ({(data.deals || []).length})</TabsTrigger>
          <TabsTrigger value="quotations" className="rounded-xl" data-testid="client-tab-quotations">Quotations ({quotes.length})</TabsTrigger>
          <TabsTrigger value="contacts" className="rounded-xl">Contacts ({(data.contacts || []).length})</TabsTrigger>
          <TabsTrigger value="followups" className="rounded-xl">Follow-ups ({(data.followups || []).length})</TabsTrigger>
          <TabsTrigger value="timeline" className="rounded-xl">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="deals">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              {(data.deals || []).length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No deals yet.</p>
              ) : data.deals.map((d) => (
                <Link key={d.id} to={`/deals/${d.id}`} className="block p-3.5 rounded-2xl border border-border/60 hover:bg-muted/30 tap transition" data-testid={`client-deal-${d.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-sm">{d.title}</p>
                      <p className="text-xs text-muted-foreground capitalize">{STAGE_LABEL[d.stage] || d.stage} · {fmtINR(d.value)}</p>
                    </div>
                    {d.next_followup_date && <span className="text-xs text-muted-foreground">{fmtDate(d.next_followup_date)}</span>}
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quotations">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              {quotes.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-sm text-muted-foreground">No quotations for this client yet.</p>
                  <Button variant="outline" onClick={() => setOpenQuote(true)} className="rounded-2xl mt-3" data-testid="new-quotation-client-empty">
                    <FileText className="h-4 w-4 mr-1" /> Create first quotation
                  </Button>
                </div>
              ) : quotes.map((q) => (
                <Link key={q.id} to={`/quotations/${q.id}`} className="block p-3.5 rounded-2xl border border-border/60 hover:bg-muted/30 tap transition" data-testid={`client-quotation-${q.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-mono text-xs font-bold tracking-wider">{q.number}</p>
                        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_COLOR[q.status] || ""}`}>{STATUS_LABEL[q.status] || q.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{q.subject || "—"} · {(q.send_history || []).length} send{(q.send_history || []).length === 1 ? "" : "s"}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-sm tabular-nums">{fmtINR(q.grand_total)}</p>
                      <p className="text-[10px] text-muted-foreground">{fmtDate(q.created_at)}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              {(data.contacts || []).length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No contacts yet.</p>
              ) : data.contacts.map((ct) => (
                <div key={ct.id} className="p-3.5 rounded-2xl border border-border/60 flex items-start gap-3" data-testid={`client-contact-${ct.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{ct.name}</p>
                      {ct.is_primary && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] gap-1" variant="outline" data-testid={`primary-badge-${ct.id}`}>
                          <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" /> Primary
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{ct.designation || "—"}</p>
                    <p className="text-xs text-muted-foreground mt-1">{ct.email || "—"} · {ct.phone || "—"}</p>
                    {!ct.is_primary && (
                      <button
                        type="button"
                        className="text-[11px] font-semibold text-primary-700 underline mt-1.5 tap"
                        onClick={async () => {
                          try {
                            await api.post(`/contacts/${ct.id}/primary`);
                            toast.success(`${ct.name} is now the primary contact — deals update automatically`);
                            load();
                          } catch (err) { toast.error(fmtError(err)); }
                        }}
                        data-testid={`set-primary-${ct.id}`}
                      >
                        Set as primary
                      </button>
                    )}
                  </div>
                  {ct.phone && (
                    <div className="flex items-center gap-1.5">
                      <CallButton phone={ct.phone} testId={`call-contact-${ct.id}`} />
                      <WhatsAppButton
                        phone={ct.phone}
                        email={ct.email}
                        contactName={ct.name}
                        clientId={c.id}
                        clientCompany={c.company_name}
                        defaultTemplate="intro"
                        label="Send"
                        testId={`wa-contact-${ct.id}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="followups">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-2">
              {(data.followups || []).length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No pending follow-ups.</p>
              ) : data.followups.map((f) => (
                <div key={f.id} className="flex items-center gap-3 p-3.5 rounded-2xl border border-border/60">
                  <CalendarClock className="h-4 w-4 text-primary" />
                  <div className="flex-1"><p className="font-semibold text-sm">{f.title}</p><p className="text-xs text-muted-foreground">{fmtDate(f.due_date)}</p></div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-4 space-y-3">
              {(data.activities || []).length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No interactions logged yet.</p>
              ) : data.activities.map((a) => (
                <div key={a.id} className="flex gap-3 p-3.5 rounded-2xl border border-border/60" data-testid={`client-activity-${a.id}`}>
                  <div className="h-8 w-8 rounded-full bg-primary-50 text-primary-700 grid place-items-center shrink-0 text-[10px] font-bold uppercase">{(a.type || "n").slice(0, 2)}</div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{a.summary}</p>
                    {a.details && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{a.details}</p>}
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mt-2">
                      {a.created_by_name || "—"} · {fmtDateTime(a.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <NewFollowupDialog open={openFu} onOpenChange={setOpenFu} defaultClientId={id} onCreated={load} />
      <BillingAddressDialog client={c} open={openAddr} onOpenChange={setOpenAddr} onSaved={load} />
      {openQuote && (
        <NewQuotationWizard
          open={openQuote}
          onClose={() => setOpenQuote(false)}
          onCreated={() => loadQuotes()}
          prefilledClientId={id}
        />
      )}
    </div>
  );
}
